/* eslint-disable sonarjs/no-duplicate-string */
import MediaIcon from '@assets/images/icons/media.png';
import { featureCollection, point } from '@turf/turf';
import { Feature, Point } from 'geojson';
import { GeoJSONSource, LngLatLike, MapLayerMouseEvent, Point as MapboxPoint } from 'mapbox-gl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Layer, Source, useMap } from 'react-map-gl';

import { MAP_LAYER_FACTS } from '@/api/rest/resources/types/fact';
import { useLayerOpacity } from '@/components/widgets/o/Map/hooks/useLayerOpacity';
import { useEffectOnce } from '@/hooks/useEffectOnce';
import { isNullish } from '@/utils/booleans';

import {
  getClusterCountLayer,
  getClusterLayer,
  getIconLayer,
  getObservationMarkerLayers,
  getObservationMarkersSourceId,
  getUnclusteredPointLayer,
  MarkerFeature,
  MEDIA_ICON_IMAGE,
  MediaMapLayerDataItem,
} from './constants';
import { MarkerPopup } from './MarkerPopup';

type MediaMapLayerProps = {
  data: MediaMapLayerDataItem[];
  configurationId: string;
};

export const MediaMapLayer = ({ data, configurationId }: MediaMapLayerProps) => {
  return <MapMarkers data={data} configurationId={configurationId} />;
};

const MapMarkers = ({ data, configurationId }: { data: MediaMapLayerDataItem[]; configurationId: string }) => {
  const [selectedPoint, setSelectedPoint] = useState<MarkerFeature | null>(null);
  const layerOpacity = useLayerOpacity(`${MAP_LAYER_FACTS.in_situ_map_layer}-${configurationId}`);

  const sourceId = getObservationMarkersSourceId(configurationId);
  const observationMarkerLayers = useMemo(() => getObservationMarkerLayers(configurationId), [configurationId]);

  // Group the data by coordinates to handle multiple markers at the same location
  const coordinateGroups = useMemo(() => {
    const groups = new Map<string, MediaMapLayerDataItem[]>();
    data.forEach((item) => {
      const key = getCoordinateKey(item);
      const group = groups.get(key) ?? [];
      group.push(item);
      groups.set(key, group);
    });
    return groups;
  }, [data]);

  const mapRef = useMap();

  /**
   * Prevents a warning from getting logged to the console when the effect is
   * executed more than once causing the `addImage` method to be invoked
   * multiple times.
   */
  useEffectOnce(
    // eslint-disable-next-line prefer-arrow-callback
    function loadPinIcon() {
      const map = mapRef.current;

      if (!map) {
        return;
      }

      if (!map.hasImage?.(MEDIA_ICON_IMAGE)) {
        map.loadImage?.(MediaIcon, (error, image) => {
          if (error || image === undefined) throw error;
          map.addImage(MEDIA_ICON_IMAGE, image, { sdf: true });
        });
      }
    },
    [],
  );

  const handleMapClick = useCallback(
    (event: MapLayerMouseEvent) => {
      const feature = event.features?.[0] as Feature<Point> | undefined;
      const map = event.target;

      if (!feature) {
        return;
      }

      /**
       * Explode the cluster and zoom in when a cluster is clicked.
       */
      const handleClusterMarkerClick = (clusterId: number) => {
        const mapboxSource = map.getSource(sourceId) as GeoJSONSource;

        mapboxSource.getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) {
            return;
          }

          map.easeTo({
            center: feature.geometry.coordinates as LngLatLike,
            zoom,
            duration: 500,
          });
        });
      };

      /**
       * Show a details popup when a marker is clicked.
       */
      const handleUnclusteredMarkerClick = () => {
        const markerPoint = feature as MarkerFeature | undefined;

        if (!markerPoint) {
          return null;
        }

        if (!isNullish(selectedPoint)) {
          map.setFeatureState({ source: sourceId, id: selectedPoint.id }, { active: false });

          if (selectedPoint.id === markerPoint.id) {
            return setSelectedPoint(null);
          }
        }

        /**
         * We do this so that the popup is first torn-down
         * and then created fresh. Without this, the popup does not update
         * when a different plot is clicked while the popup is open.
         */
        setSelectedPoint(null);
        setTimeout(() => {
          setSelectedPoint(markerPoint);
        }, 0);

        map.setFeatureState({ source: sourceId, id: markerPoint.id }, { active: true });

        map.easeTo({
          center: feature.geometry.coordinates as LngLatLike,
          duration: 500,
          offset: new MapboxPoint(-200, 0),
        });

        return null;
      };

      const clusterId = feature.properties?.cluster_id;

      if (clusterId) {
        handleClusterMarkerClick(clusterId);
      } else {
        handleUnclusteredMarkerClick();
      }
    },
    [selectedPoint, sourceId],
  );

  const hoveredPointIdRef = useRef<number | null>(null);

  const handleMapMouseMove = useCallback(
    (event: MapLayerMouseEvent) => {
      const map = event.target;

      if (!map) return;

      map.getCanvas().style.cursor = 'pointer';

      const feature = event.features?.[0];

      const featureId = feature?.id;

      if (!featureId) {
        return;
      }

      if (hoveredPointIdRef.current !== null) {
        map.setFeatureState({ source: sourceId, id: hoveredPointIdRef.current as number }, { hover: false });
      }

      hoveredPointIdRef.current = featureId as number;
      map.setFeatureState({ source: sourceId, id: hoveredPointIdRef.current as number }, { hover: true });
    },
    [sourceId],
  );

  const handleMapMouseLeave = useCallback(
    (event: MapLayerMouseEvent) => {
      const map = event.target;

      if (!map) return;

      map.getCanvas().style.cursor = '';

      if (hoveredPointIdRef.current !== null) {
        map.setFeatureState({ source: sourceId, id: hoveredPointIdRef.current as number }, { hover: false });
        hoveredPointIdRef.current = null;
      }
    },
    [sourceId],
  );

  useEffect(
    // eslint-disable-next-line prefer-arrow-callback
    function addHoverHandler() {
      const map = mapRef.current;

      if (!map) {
        return undefined;
      }

      map.on('click', observationMarkerLayers, handleMapClick);
      map.on('mousemove', observationMarkerLayers, handleMapMouseMove);
      map.on('mouseleave', observationMarkerLayers, handleMapMouseLeave);

      return () => {
        map.off('click', observationMarkerLayers, handleMapClick);
        map.off('mousemove', observationMarkerLayers, handleMapMouseMove);
        map.off('mouseleave', observationMarkerLayers, handleMapMouseLeave);
      };
    },
    [mapRef, handleMapMouseMove, handleMapMouseLeave, handleMapClick, observationMarkerLayers],
  );

  const markers = featureCollection(
    Array.from(coordinateGroups.entries()).flatMap(([, items], index) => {
      const first = items[0]; // Prevent duplicate markers at the same coordinates
      if (!first) return [];
      return [point([parseFloat(first.longitude), parseFloat(first.latitude)], first, { id: index })];
    }),
  );

  const selectedGroup = selectedPoint
    ? (coordinateGroups.get(getCoordinateKey(selectedPoint.properties)) ?? null)
    : null;

  return (
    <>
      {/* clusterMaxZoom=15: clusterMaxZoom is set below app maxZoom (16) so zooming in fully reveals individual markers */}
      <Source id={sourceId} type='geojson' data={markers} cluster={true} clusterMaxZoom={15}>
        <Layer
          {...getClusterLayer(configurationId)}
          paint={{
            ...getClusterLayer(configurationId).paint,
            'circle-opacity': ['*', ['case', ['boolean', ['feature-state', 'hover'], false], 1.0, 0.8], layerOpacity],
            'circle-stroke-opacity': [
              '*',
              ['case', ['boolean', ['feature-state', 'hover'], false], 0.6, 0.4],
              layerOpacity,
            ],
          }}
        />
        <Layer {...getClusterCountLayer(configurationId)} paint={{ 'text-opacity': layerOpacity }} />
        <Layer
          {...getUnclusteredPointLayer(configurationId)}
          paint={{
            ...getUnclusteredPointLayer(configurationId).paint,
            'circle-opacity': [
              '*',
              [
                'case',
                ['boolean', ['feature-state', 'hover'], false],
                1.0,
                ['boolean', ['feature-state', 'active'], false],
                1.0,
                0.8,
              ],
              layerOpacity,
            ],
            'circle-stroke-opacity': [
              '*',
              [
                'case',
                ['boolean', ['feature-state', 'hover'], false],
                0.6,
                ['boolean', ['feature-state', 'active'], false],
                0.6,
                0.4,
              ],
              layerOpacity,
            ],
          }}
        />
        <Layer
          {...getIconLayer(configurationId)}
          paint={{ 'icon-opacity': layerOpacity, 'text-opacity': layerOpacity }}
        />
      </Source>

      {selectedGroup && <MarkerPopup markerItems={selectedGroup} onClose={() => setSelectedPoint(null)} />}
    </>
  );
};

const getCoordinateKey = (item: MediaMapLayerDataItem) => `${item.latitude},${item.longitude}`;
